<script lang="ts">
	import {
		input,
		newAccoutLeverage,
		maxInputAmount,
		minInputAmount,
		newPositionGradient,
		accountLeverage,
		inputUnit
	} from './store';
	import { getPostTradeDetails } from '$lib/data/userInteract';

	import BigNumber from 'bignumber.js';
	import { debounce, getNumBackInUnits, sanitizeInputs } from '$lib/util/util';
	import { onDestroy, onMount } from 'svelte';
	import type { InputUnit } from '$lib/types/userInteract';
	import { currentMarket, currentMarketAsset, maxLeverage } from '../store';
	import { slide } from 'svelte/transition';
	import Tooltip from '$lib/tooltip/Tooltip.svelte';

	let localinput: string;
	export let insufficientMargin;
	let currentMarketEventListener = () => {};

	function onInputChange() {
		localinput = sanitizeInputs(localinput);
		let processingInput = localinput ? localinput : '0';

		if ($inputUnit == 'USD') {
			insufficientMargin = new BigNumber(processingInput)
				.multipliedBy(1e18)
				.gt(new BigNumber($maxInputAmount));
			$input = new BigNumber(processingInput).multipliedBy(1e18).toString();
		} else if ($inputUnit == 'NATIVE' && $currentMarket) {
			insufficientMargin = new BigNumber(processingInput)
				.multipliedBy($currentMarket.currentMarketPrice)
				.gt(new BigNumber($maxInputAmount));
			$input = new BigNumber(processingInput)
				.multipliedBy($currentMarket.currentMarketPrice)
				.toString();
		}
	}

	function setInputUnit(unit: InputUnit) {
		if ($inputUnit != unit) {
			$inputUnit = unit;
			let processingInput = localinput ? localinput : '0';
			currentMarketAssetEventListener();
			if ($currentMarket) {
				if (unit == 'USD') {
					localinput = getNumBackInUnits(
						new BigNumber(processingInput)
							.multipliedBy($currentMarket.currentMarketPrice)
							.toString(),
						18,
						4
					);
				} else {
					localinput = new BigNumber(processingInput)
						.div(new BigNumber($currentMarket.currentMarketPrice).div(1e18))
						.toFixed(4)
						.toString();
					currentMarketAssetEventListener = currentMarket.subscribe((market) => {
						if (market) {
							$input = new BigNumber(localinput || '0').multipliedBy(market.currentMarketPrice).toString();
						}
					});
				}
			}

			onInputChange();
			getPostTradeDetails();
		}
	}

	function onLeverageChange() {
		if ($input > $maxInputAmount) {
			$input = $maxInputAmount;
		}
		if ($inputUnit == 'USD') {
			localinput = getNumBackInUnits($input, 18, 2);
		} else if ($inputUnit == 'NATIVE') {
			localinput = new BigNumber($input)
				.div(new BigNumber($currentMarket?.currentMarketPrice as string))
				.toFixed(6);
		}
	}
	let inputSliderWidth = 0;

	$: if (
		new BigNumber($input)
			.minus(new BigNumber($minInputAmount))
			.div(new BigNumber($maxInputAmount).minus(new BigNumber($minInputAmount)))
			.multipliedBy(100)
			.toNumber()
	) {
		inputSliderWidth = Math.min(
			new BigNumber($input)
				.minus(new BigNumber($minInputAmount))
				.div(new BigNumber($maxInputAmount).minus(new BigNumber($minInputAmount)))
				.multipliedBy(100)
				.toNumber(),
			100
		);
	} else {
		inputSliderWidth = 0;
	}

	let currentMarketAssetEventListener = () => {};

	onMount(() => {
		currentMarketAssetEventListener = currentMarketAsset.subscribe(() => {
			localinput = '';
			$input = '0';
		});
	});

	onDestroy(() => {
		currentMarketAssetEventListener();
		currentMarketEventListener();
	});
</script>

<div
	class="mt-4 flex justify-between items-center w-full border border-fx-grey-500 bg-fx-gradient-8 rounded-lg squircle-lg py-2 px-3"
>
	<input
		inputmode="decimal"
		autocomplete="off"
		autocorrect="off"
		pattern="^[0-9]*[.,]?[0-9]*$"
		minlength="1"
		maxlength="79"
		spellcheck="false"
		bind:value={localinput}
		on:input={onInputChange}
		use:debounce={{ localinput, func: getPostTradeDetails, duration: 300 }}
		type="string"
		placeholder="0"
		data-testid="open-position-amount"
		class="bg-transparent text-fx-white font-medium outline-none text-sm"
	/>

	<div class="text-fx-grey-200 text-xs flex gap-2">
		<div class="text-fx-grey-400 border-r border-fx-grey-400 pr-2 pt-px">Size</div>
		<div
			class="px-1 py-0.5 rounded-sm cursor-pointer"
			class:bg-fx-grey-600={$inputUnit == 'NATIVE'}
			on:click={() => {
				setInputUnit('NATIVE');
			}}
			on:keydown={() => {
				setInputUnit('NATIVE');
			}}
			role="presentation"
		>
			{$currentMarketAsset}
		</div>
		<div
			class="px-1 py-0.5 rounded-sm cursor-pointer"
			class:bg-fx-grey-600={$inputUnit == 'USD'}
			on:click={() => {
				setInputUnit('USD');
			}}
			on:keydown={() => {
				setInputUnit('USD');
			}}
			role="presentation"
		>
			USD
		</div>
	</div>
</div>
<div class="flex w-full justify-between mb-3 mt-4">
	<div
		class="text-fx-grey-400 text-xs align-middle pt-2"
		data-title="Account Leverage"
		data-intro="This is the oerall account leverage after trade wil be executed."
		data-step="5"
	>
		Account Leverage
	</div>
	<div class="text-sm bg-fx-grey-800 px-3 py-1.5 rounded-lg squircle-lg flex items-center gap-1">
		<span class={$newPositionGradient}>
			{Math.abs($newAccoutLeverage).toFixed(2)}x
		</span>
		{#if new BigNumber($newAccoutLeverage).gte(new BigNumber($maxLeverage).multipliedBy(0.95))}
			<Tooltip
				title="Orders with high leverage can cause the execution to differ from the intended price level, resulting in slippage. This can cause your order to expire."
				header="Your order may expire"
			>
				<img
					loading="lazy"
					src={'caution.png'}
					alt="Caution"
					class="w-3 h-3 ml-1 animate-bounce"
					transition:slide
				/>
			</Tooltip>
		{/if}
	</div>
</div>
<div class="w-full">
	<div class="flex justify-start w-full -mb-4">
		<div class=" rounded-xl h-1 bg-fx-gradient-20" style="width:{inputSliderWidth}%" />
	</div>
	<input
		id="steps-range"
		type="range"
		min={$minInputAmount}
		max={Math.max(Number($maxInputAmount), Number($input))}
		bind:value={$input}
		on:input={onLeverageChange}
		step="100000000000000000"
		class="slider w-full h-1 bg-fx-grey-300 rounded-lg appearance-none cursor-pointer mt-3 bg-opacity-10"
	/>
</div>
<div class="flex justify-between text-xs">
	{#if $accountLeverage < 5}
		<div class="flex gap-1 flex-col">
			<div class="text-fx-gradient-23">Safe</div>
		</div>
	{/if}
	<div class="flex gap-1 flex-col" class:items-center={$accountLeverage < 5}>
		<div class="text-fx-yellow">Risky</div>
	</div>
	<div class="flex gap-1 flex-col items-end">
		<div class="text-fx-gradient-22">Danger</div>
	</div>
</div>

<style>
	.margin-slider-dot {
		min-width: 12px !important;
	}

	.active-leverage {
		@apply text-fx-grey-900 font-semibold;
		background: linear-gradient(
			135.51deg,
			#ffe298 9.84%,
			#d1a3ff 38%,
			#43a5ff 65.74%,
			#58ffc3 91.77%
		);
	}
	.slider::-webkit-slider-thumb {
		@apply w-4 h-4 cursor-pointer appearance-none rounded-full opacity-100;
		background: linear-gradient(
			135.51deg,
			#ffe298 9.84%,
			#d1a3ff 38%,
			#43a5ff 65.74%,
			#58ffc3 91.77%
		);
	}
	/* Chrome, Safari, Edge, Opera */
	input::-webkit-outer-spin-button,
	input::-webkit-inner-spin-button {
		-webkit-appearance: none;
		margin: 0;
	}
</style>
